/**
 * AuthForm Component
 *
 * Reusable authentication form with email/password inputs and Google OAuth button.
 * Includes password visibility toggle, error handling, and loading states.
 */

import { useState, FormEvent, useRef } from 'react';
import Button from '@components/Button';
import Modal from '@components/Modal';

export interface AuthFormProps {
  mode: 'signin' | 'signup';
  onSubmit: (email: string, password: string) => Promise<void>;
  onGoogleSignIn: () => Promise<void>;
  onForgotPassword?: (email: string) => Promise<void>;
  showPasswordStrength?: boolean;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

export default function AuthForm({
  mode,
  onSubmit,
  onGoogleSignIn,
  onForgotPassword,
  showPasswordStrength = false,
}: AuthFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetModalMessage, setResetModalMessage] = useState<string>('');
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  const isEmailValid = (value: string) => {
    const trimmed = value.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  };

  const calculatePasswordStrength = (pwd: string): PasswordStrength => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;

    const labels: { label: string; color: string }[] = [
      { label: 'Too weak', color: 'bg-red-500' },
      { label: 'Weak', color: 'bg-orange-500' },
      { label: 'Fair', color: 'bg-yellow-500' },
      { label: 'Good', color: 'bg-lime-500' },
      { label: 'Strong', color: 'bg-green-500' },
      { label: 'Very strong', color: 'bg-emerald-600' },
    ];

    const strength = labels[Math.min(score, labels.length - 1)]!;
    return { score, label: strength.label, color: strength.color };
  };

  const passwordStrength =
    showPasswordStrength && password
      ? calculatePasswordStrength(password)
      : null;

  const passwordRequirements = (pwd: string) => {
    return {
      length8: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[^a-zA-Z0-9]/.test(pwd),
    };
  };

  const reqs = showPasswordStrength ? passwordRequirements(password) : null;

  const getErrorMessage = (
    error: unknown,
    fallback = 'An error occurred. Please try again.'
  ) => {
    if (error instanceof Error) return error.message || fallback;
    if (typeof error === 'string' && error.trim()) return error;
    if (
      error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof (error as Record<string, unknown>).message === 'string'
    ) {
      return ((error as Record<string, unknown>).message as string) || fallback;
    }
    return fallback;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setEmailError(null);
    setLoading(true);

    if (!isEmailValid(email)) {
      setEmailError('Please enter a valid email address.');
      setLoading(false);
      if (emailInputRef.current) {
        emailInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailInputRef.current.focus();
      }
      return;
    }

    try {
      if (mode === 'signup' && password !== confirmPassword) {
        setError('Passwords do not match.');
        setLoading(false);
        return;
      }

      await onSubmit(email, password);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      await onGoogleSignIn();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'An error occurred with Google sign-in.'));
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      if (onForgotPassword) {
        await onForgotPassword(email);
        setShowForgotPassword(false);
        setResetModalMessage(
          'If an account exists for that email, you will receive a password reset email. Password reset email sent! Please check your inbox.'
        );
        setShowResetModal(true);
      }
    } catch (err: unknown) {
      // If the backend indicates the email is not registered, show that in the modal
      const msg = getErrorMessage(err, 'Failed to send reset email.');
      const lower = String(msg).toLowerCase();
      const isNotFound =
        lower.includes('user') &&
        (lower.includes('not') || lower.includes('found'));

      if (isNotFound) {
        // Use a non-revealing, generic message when email may not exist
        setShowForgotPassword(false);
        setResetModalMessage(
          'If an account exists for that email, you will receive a password reset email. Please check your inbox.'
        );
        setShowResetModal(true);
      } else {
        setError(msg || 'Failed to send reset email.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {error && (
        <div className="animate-fade-in mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      {showForgotPassword ? (
        <div className="animate-fade-in space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Reset Password
          </h3>
          <p className="text-sm text-gray-600">
            Enter your email address and we'll send you a link to reset your
            password.
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            disabled={loading}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleForgotPassword}
              disabled={loading}
              variant="primary"
              size="md"
              className="begin-breathing-button flex-1"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
            <button
              onClick={() => setShowForgotPassword(false)}
              disabled={loading}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-3 font-medium text-gray-700 transition-all hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmail(value);
                  if (emailError && isEmailValid(value)) {
                    setEmailError(null);
                  }
                }}
                ref={emailInputRef}
                placeholder="Enter your email"
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-3 transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                disabled={loading}
              />
              {emailError && (
                <p className="mt-2 text-sm text-red-600">{emailError}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  minLength={6}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 transition-colors hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {passwordStrength && (
                <div className="animate-fade-in mt-2">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Password Strength
                    </span>
                    <span className="text-sm font-semibold text-gray-800">
                      {passwordStrength.label}
                    </span>
                  </div>

                  <div className="mb-3 h-2 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className={`h-full ${passwordStrength.color} transition-all duration-300`}
                      style={{
                        width: `${(passwordStrength.score / 5) * 100}%`,
                      }}
                    />
                  </div>

                  {reqs && (
                    <div className="mb-3 text-sm text-gray-700">
                      <div className="mb-2">Requirements:</div>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${reqs.length8 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {reqs.length8 ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="9"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            className={`text-sm ${reqs.length8 ? 'text-green-600' : 'text-gray-600'}`}
                          >
                            At least 8 characters
                          </span>
                        </li>

                        <li className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${reqs.uppercase ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {reqs.uppercase ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="9"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            className={`text-sm ${reqs.uppercase ? 'text-green-600' : 'text-gray-600'}`}
                          >
                            One uppercase letter (A-Z)
                          </span>
                        </li>

                        <li className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${reqs.lowercase ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {reqs.lowercase ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="9"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            className={`text-sm ${reqs.lowercase ? 'text-green-600' : 'text-gray-600'}`}
                          >
                            One lowercase letter (a-z)
                          </span>
                        </li>

                        <li className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${reqs.number ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {reqs.number ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="9"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            className={`text-sm ${reqs.number ? 'text-green-600' : 'text-gray-600'}`}
                          >
                            One number (0-9)
                          </span>
                        </li>

                        <li className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${reqs.special ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {reqs.special ? (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="9"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            className={`text-sm ${reqs.special ? 'text-green-600' : 'text-gray-600'}`}
                          >
                            One special character (!@#$%^&*...)
                          </span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {mode === 'signup' && (
                <div className="mt-4">
                  <label
                    htmlFor="confirm-password"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Confirm Password
                  </label>
                  <input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    required={mode === 'signup'}
                    minLength={6}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    disabled={loading}
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="mt-2 text-sm text-red-600">
                      Passwords do not match.
                    </p>
                  )}
                </div>
              )}
            </div>

            {mode === 'signin' && onForgotPassword && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-indigo-600 transition-colors hover:text-indigo-700 hover:underline"
                  disabled={loading}
                >
                  Forgot password?
                </button>
              </div>
            )}

            {mode === 'signup' ? (
              <>
                {/* Footer Note */}
                <p className="mt-8 text-center text-xs text-gray-500">
                  By signing up, you agree to Xhalin's{' '}
                  <a href="/terms" className="text-indigo-600 hover:underline">
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a
                    href="/privacy"
                    className="text-indigo-600 hover:underline"
                  >
                    Privacy Policy
                  </a>
                </p>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="begin-breathing-button w-full"
                  disabled={loading}
                >
                  {loading ? 'Please wait...' : 'Sign Up'}
                </Button>
              </>
            ) : (
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="begin-breathing-button w-full"
                disabled={loading}
              >
                {loading ? 'Please wait...' : 'Sign In'}
              </Button>
            )}
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-gray-500">
                or continue with
              </span>
            </div>
          </div>

          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="flex w-full transform items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 transition-all hover:scale-[1.02] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-gray-100"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>
          {showResetModal && (
            <Modal
              title="Password Reset"
              onClose={() => setShowResetModal(false)}
            >
              <div className="space-y-4">
                <p className="text-sm text-gray-700">{resetModalMessage}</p>
                <div className="mt-4 flex justify-end">
                  <Button
                    type="button"
                    onClick={() => setShowResetModal(false)}
                  >
                    OK
                  </Button>
                </div>
              </div>
            </Modal>
          )}
        </>
      )}
    </div>
  );
}
